import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { User } from '@/core/models/user.model';
import KEYS from '@/values/keys';
import { LoginParams } from '../params/auth.params';
import { AuthPrivileges } from '../models/auth-privileges.model';
import Vue from 'vue';
import { AUTH_USER_TYPE, USER_TYPE } from '../values/auth.values';
import { AuthProvider } from '../providers/auth.provider';
import { ROLES } from '../enums/auth.enum';

export const AUTH_STORE_NAME = 'AuthStore';

export const AUTH_STORE_MUTATIONS = {
  SET_USER_INFO: 'setUserInfo',
  SET_SESSION: 'setSession',
  SET_USER_ROLE: 'setUserRole',
  SET_PRIVILEGES: 'setPrivileges',
  SET_SERVERS_URL: 'setServersUrl',
}

export const AUTH_STORE = {
  MUTATIONS: {
    SET_USER_INFO: `${AUTH_STORE_NAME}/${AUTH_STORE_MUTATIONS.SET_USER_INFO}`,
    SET_SESSION: `${AUTH_STORE_NAME}/${AUTH_STORE_MUTATIONS.SET_SESSION}`,
    SET_USER_ROLE: `${AUTH_STORE_NAME}/${AUTH_STORE_MUTATIONS.SET_USER_ROLE}`,
    SET_PRIVILEGES: `${AUTH_STORE_NAME}/${AUTH_STORE_MUTATIONS.SET_PRIVILEGES}`,
    SET_SERVERS_URL: `${AUTH_STORE_NAME}/${AUTH_STORE_MUTATIONS.SET_SERVERS_URL}`,
  },
  ACTIONS: {
    LOGIN: `${AUTH_STORE_NAME}/login`,
    GET_USER_DATA: `${AUTH_STORE_NAME}/getUserData`,
  }
}

@Module({ namespaced: true })
export class AuthStore extends VuexModule {

  /* State */
  public socketUrl: string = '';
  public wboTaskSocketUrl: string = process.env.VUE_APP_WBO_TASK_URL;
  public edupadUrl: string = process.env.VUE_APP_EDUAC_PAD_URL;
  public screenShareUrl: string = '';
  public user: User = new User();
  /* 
    Important: 'role' attr should be deprecated on the future.  
    If we need to take the role, we should use 'userRoles' instead.
  */
  public role: string = null;
  public userRoles: string[] = [];
  public privileges: AuthPrivileges = new AuthPrivileges();
  public hasSession: boolean = false;
  public token : string = '';

  /* Getters */
  get isPublisher() {
    return this.role == KEYS.ROLES.PUBLISHER;
  }

  get isStudent() {
    return this.userRoles.includes(ROLES.EDUAC_STUDENT);
  }

  get isAuthor() {
    return this.userRoles.includes(ROLES.EDUAC_AUTHOR);
  }

  get isSubstituteTeacher() {
    return this.isPublisher && this.privileges.SubstituteTeacher;
  }

  get isOnlySubstituteTeacher() {
    return this.userRoles.includes(ROLES.EDUAC_SUBSTITUTE_TEACHER);
  }

  /* Mutations */
  @Mutation
  public setUserInfo(user: User) {
    const isPublisher = AUTH_USER_TYPE.PUBLISHER.includes(user.role);
    const role = isPublisher ? KEYS.ROLES.PUBLISHER : KEYS.ROLES.BUYER;
    this.role = role;
    this.user = user;
  }

  @Mutation
  public setSession() {
    this.userRoles = AuthProvider.getRolesFromAccessToken();
    this.privileges = AuthProvider.getPrivilegesFromAccessToken(); 
    this.hasSession = true;
    this.token = AuthProvider.getToken();
  }

  @Mutation
  public cleanSession() {
    this.hasSession = false;
    this.user = new User();
    this.role = null;
    this.privileges = new AuthPrivileges();
  }

  @Mutation
  public setToken(token: string) {
    this.token = token
  }

  @Mutation
  public setServersUrl(args : {screenShareUrl: string}) {
    this.screenShareUrl = args.screenShareUrl;
  }

  /* Actions */
  @Action({rawError: true})
  public async login(data: LoginParams): Promise<void> {
    await Vue.prototype.$Auth.Services.login(data);
    
    this.context.commit(AUTH_STORE_MUTATIONS.SET_SESSION);
  }

  @Action({rawError: true})
  public async getUserLogged() {
    const user = await Vue.prototype.$Auth.Services.getUserLogged();
    this.context.commit(AUTH_STORE_MUTATIONS.SET_USER_INFO, user);
  }

  /* Initial State */
  @Mutation
  public initState(state) {
    this.socketUrl = '';
    this.wboTaskSocketUrl = process.env.VUE_APP_WBO_TASK_URL;
    this.edupadUrl = process.env.VUE_APP_EDUAC_PAD_URL;
    this.screenShareUrl = '';
    this.user = new User();
    this.role = null;
    this.userRoles = [];
    this.privileges = new AuthPrivileges();
    this.hasSession = false;
    this.token = '';
  }

}